// =============================================================================
// Workspace Detail Page Component
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { workspaceService } from '../services/workspaceService';
import LoadingSpinner from '../components/LoadingSpinner';
import WorkspaceManager from '../components/WorkspaceManager';
import toast from 'react-hot-toast';

const WorkspaceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadWorkspace();
  }, [id]);

  const loadWorkspace = async () => {
    try {
      const workspaceData = await workspaceService.getWorkspace(id);
      setWorkspace(workspaceData);
    } catch (error) {
      console.error('Failed to load workspace:', error);
      toast.error('Failed to load workspace');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this workspace? This action cannot be undone.')) {
      return;
    }

    try {
      await workspaceService.deleteWorkspace(id);
      toast.success('Workspace deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Failed to delete workspace');
    }
  };

  const getTemplateIcon = (templateId) => {
    switch (templateId) {
      case 'nodejs': return '🟢';
      case 'python': return '🐍';
      case 'go': return '🔵';
      case 'rust': return '🦀';
      default: return '📦';
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Workspace not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-ghost btn-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <div className="flex items-center gap-3">
            <span className="text-3xl">{getTemplateIcon(workspace.templateId)}</span>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{workspace.name}</h1>
              <p className="text-muted-foreground">{workspace.description}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            className="btn btn-ghost text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* 集成的工作空间管理器 */}
      <WorkspaceManager 
        workspace={workspace} 
        onWorkspaceUpdate={loadWorkspace}
      />
    </div>
  );
};

export default WorkspaceDetailPage;
